import { useEffect, useState } from 'react';
import { Search, User, Box, Ticket, GraduationCap, ShieldCheck, Cloud,
         Server, ScrollText, FileBarChart, FileText, FileSpreadsheet, Download,
         Gauge, AlertTriangle, CheckCircle2, Ghost, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const TABS = [
  { key: 'summary',  label: 'Access Review',    Icon: Gauge },
  { key: 'hrms',     label: 'HRMS Summary',     Icon: User },
  { key: 'assets',   label: 'Assets',           Icon: Box },
  { key: 'tickets',  label: 'Tickets',          Icon: Ticket },
  { key: 'training', label: 'Training',         Icon: GraduationCap },
  { key: 'entra',    label: 'Entra Access',     Icon: ShieldCheck },
  { key: 'aws',      label: 'AWS Access',       Icon: Cloud },
  { key: 'svc',      label: 'Service Accounts', Icon: Server },
  { key: 'audit',    label: 'Audit History',    Icon: ScrollText },
  { key: 'reports',  label: 'Reports',          Icon: FileBarChart },
];

export default function EmployeeAccessRecords() {
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ matched: 0, entra_only: 0, hrms_only: 0, total: 0 });
  const [q, setQ] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');  // all | matched | entra_only | hrms_only
  const [selected, setSelected] = useState(null);
  const [record, setRecord] = useState(null);
  const [tab, setTab] = useState('summary');
  const [creatingTicket, setCreatingTicket] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (sourceFilter !== 'all') params.set('source', sourceFilter);
        const { data } = await api.get(`/employee-access/employees?${params}`);
        setEmployees(data || []);
      } catch { setEmployees([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, sourceFilter]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/employee-access/employees/stats'); setStats(data); }
      catch { /* keep zero */ }
    })();
  }, [employees.length]);

  useEffect(() => {
    if (!selected) return;
    (async () => {
      try { const { data } = await api.get(`/employee-access/${encodeURIComponent(selected.id)}`); setRecord(data); }
      catch { /* keep prior record */ }
    })();
  }, [selected]);

  const createGhostTicket = async () => {
    if (!selected || !selected.id?.startsWith('entra:')) return;
    setCreatingTicket(true);
    try {
      const { data } = await api.post(`/employee-access/${encodeURIComponent(selected.id)}/create-ghost-ticket`);
      if (data.already_existed) {
        toast.info(`Open ticket ${data.ticket_no} already exists for this user.`);
      } else {
        toast.success(`Ticket ${data.ticket_no} created. Track it in the Tickets module.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create ticket');
    } finally { setCreatingTicket(false); }
  };

  return (
    <div className="space-y-5" data-testid="employee-access-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
          <a href="/security" className="hover:text-[#3D4A8E]">Security &amp; Governance</a> · Employee Access Records
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1 inline-flex items-center gap-2">
          <User className="w-7 h-7 text-[#3D4A8E]" /> Employee Access Records
        </h1>
        <p className="text-slate-500 mt-1">
          <b>Entra-first 360° view.</b> Every Entra account is listed and merged with HRMS by email.
          Accounts in Entra but missing from HRMS are flagged as <b>ghosts</b> — open a ticket directly from here to fix them.
        </p>
        {/* Source counts */}
        <div className="flex flex-wrap gap-2 mt-3 items-center" data-testid="ear-source-chips">
          {[
            { k: 'all', l: 'All', count: stats.total, cls: 'bg-slate-100 text-slate-700' },
            { k: 'matched', l: 'Matched (Entra+HRMS)', count: stats.matched, cls: 'bg-emerald-50 text-emerald-700' },
            { k: 'entra_only', l: 'Ghost (Entra only)', count: stats.entra_only, cls: 'bg-rose-50 text-rose-700' },
            { k: 'hrms_only', l: 'HRMS only', count: stats.hrms_only, cls: 'bg-amber-50 text-amber-700' },
          ].map(s => (
            <button key={s.k} onClick={() => setSourceFilter(s.k)}
                    data-testid={`ear-filter-${s.k}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border ${s.cls} ${sourceFilter === s.k ? 'ring-2 ring-[#3D4A8E] border-transparent' : 'border-transparent'}`}>
              {s.l}: <b>{s.count}</b>
            </button>
          ))}
          <div className="ml-auto flex flex-wrap gap-2">
            <a href={`/api/employee-access/reports/workforce-compliance?format=pdf&auth=${localStorage.getItem('hrms_token')}`}
               data-testid="ear-workforce-pdf"
               className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100">
              <FileText className="w-3.5 h-3.5" /> Workforce Report (PDF)
            </a>
            <a href={`/api/employee-access/reports/workforce-compliance?format=xlsx&auth=${localStorage.getItem('hrms_token')}`}
               data-testid="ear-workforce-xlsx"
               className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
        {/* Picker */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden h-fit lg:sticky lg:top-4">
          <div className="p-3 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employees / Entra users" className="rounded-xl pl-9" data-testid="ear-search" />
            </div>
          </div>
          <div className="max-h-[680px] overflow-y-auto divide-y divide-slate-100">
            {employees.map((e) => (
              <button
                key={e.id}
                onClick={() => { setSelected(e); setRecord(null); setTab('summary'); }}
                data-testid={`ear-emp-${e.id}`}
                className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 ${selected?.id === e.id ? 'bg-indigo-50' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-slate-900 truncate">{e.name || '(no name)'}</div>
                    <div className="text-[11px] text-slate-500 truncate">{e.email} · {e.department || '—'}</div>
                  </div>
                  <SourcePill src={e.source} />
                </div>
                <div className="text-[10px] mt-1 inline-flex items-center gap-2">
                  {e.employee_id && <span className="text-slate-400 font-mono">{e.employee_id}</span>}
                  {e.terminated && <span className="text-rose-600 font-semibold">TERMINATED</span>}
                  {e.entra && e.entra.account_enabled === false && <span className="text-slate-500">ENTRA DISABLED</span>}
                  {e.entra && !e.entra.mfa_enabled && <span className="text-amber-600">NO MFA</span>}
                </div>
              </button>
            ))}
            {employees.length === 0 && <div className="p-4 text-xs text-slate-400">No users match. Try clearing filters.</div>}
          </div>
        </div>

        {/* Detail */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          {!selected && <div className="text-sm text-slate-400 text-center py-12">Select an employee from the list to load the 360° record.</div>}
          {selected && !record && <div className="text-sm text-slate-500 text-center py-12">Loading…</div>}
          {selected && record && (
            <div className="space-y-4">
              {/* GHOST BANNER */}
              {record.hrms.ghost && (
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start gap-3" data-testid="ear-ghost-banner">
                  <Ghost className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-rose-900">Ghost identity detected</div>
                    <div className="text-xs text-rose-800 mt-0.5">
                      This Entra account ({record.entra?.upn || record.entra?.email}) has <b>no matching HRMS record</b>.
                      Either create an HRMS record for this user or disable the Entra account.
                      Data shown below comes entirely from Entra + auditable footprint.
                    </div>
                  </div>
                  <Button onClick={createGhostTicket} disabled={creatingTicket}
                          data-testid="ear-ghost-create-ticket"
                          className="rounded-full bg-rose-600 hover:bg-rose-700 text-white flex-shrink-0">
                    {creatingTicket ? 'Creating…' : 'Create HRMS-missing ticket'}
                  </Button>
                </div>
              )}

              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-heading text-xl font-bold inline-flex items-center gap-2">
                    {record.hrms.name}
                    <SourcePill src={record.hrms.ghost ? 'entra_only' : (selected.source || 'matched')} />
                  </div>
                  <div className="text-xs text-slate-500">
                    {record.hrms.employee_id || (record.hrms.ghost && '(no employee id)')} · {record.hrms.designation} · {record.hrms.department}
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${record.hrms.employment_status === 'Active' ? 'bg-emerald-50 text-emerald-700' : record.hrms.ghost ? 'bg-rose-50 text-rose-700' : 'bg-rose-50 text-rose-700'}`}>
                      {record.hrms.employment_status}
                    </span>
                  </div>
                </div>
                {record.summary && (
                  <div className="flex items-center gap-2" data-testid="ear-summary-headline">
                    <div className={`px-3 py-1.5 rounded-xl text-sm font-bold ${scoreColor(record.summary.overall_compliance_score)}`}>
                      {record.summary.overall_compliance_score}% Compliance
                    </div>
                    <div className={`px-3 py-1.5 rounded-xl text-sm font-bold ${riskColor(record.summary.overall_risk_rating)}`}>
                      Risk: {record.summary.overall_risk_rating}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-1 border-b border-slate-200 overflow-x-auto">
                {TABS.map((t) => (
                  <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ear-tab-${t.key}`}
                    className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-px whitespace-nowrap ${tab === t.key ? 'border-[#3D4A8E] text-[#3D4A8E]' : 'border-transparent text-slate-500 hover:text-slate-900'}`}>
                    <t.Icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                ))}
              </div>

              {tab === 'summary' && <SummaryPanel s={record.summary} hrms={record.hrms} />}
              {tab === 'hrms' && <HrmsPanel d={record.hrms} />}
              {tab === 'assets' && <Table rows={record.assets} empty="No assets assigned" cols={[['asset_id','ID'],['name','Name'],['serial_number','Serial No.'],['category','Category'],['status','Status'],['acknowledgement_status','Ack'],['assigned_at','Assigned']]} dateCols={['assigned_at']} />}
              {tab === 'tickets' && <Table rows={record.tickets} empty="No tickets" cols={[['ticket_no','Ticket'],['title','Title'],['status','Status'],['priority','Priority'],['sla_breached','SLA?'],['created_at','Created']]} dateCols={['created_at']} />}
              {tab === 'training' && <TrainingPanel d={record.training} />}
              {tab === 'entra' && <KvPanel d={record.entra} note={record.entra.matched ? null : 'No Entra account matched. Verify work email is set on the HRMS profile.'} />}
              {tab === 'aws' && <KvPanel d={record.aws} note={record.aws.matched ? null : 'No AWS IAM user matched.'} />}
              {tab === 'svc' && <Table rows={record.service_accounts} empty="No service accounts / apps / managed identities owned" cols={[['kind','Type'],['name','Resource'],['role','Role'],['permission_count','Perms'],['review_status','Review'],['next_review_date','Next Review']]} dateCols={['next_review_date']} />}
              {tab === 'audit' && <Table rows={record.audit} empty="No audit events" cols={[['ts','When'],['action','Action'],['module','Module'],['severity','Severity'],['target_name','Target']]} dateCols={['ts']} />}
              {tab === 'reports' && <ReportsPanel id={selected.id} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Helpers ----------
function scoreColor(score) {
  if (score == null) return 'bg-slate-100 text-slate-700 border border-slate-200';
  if (score >= 80) return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (score >= 50) return 'bg-amber-50 text-amber-700 border border-amber-200';
  return 'bg-rose-50 text-rose-700 border border-rose-200';
}

function riskColor(level) {
  const l = (level || '').toLowerCase();
  if (l === 'low') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (l === 'medium') return 'bg-amber-50 text-amber-700 border border-amber-200';
  if (l === 'high') return 'bg-rose-50 text-rose-700 border border-rose-200';
  return 'bg-slate-100 text-slate-700 border border-slate-200';
}

function sevPillClass(sev) {
  const s = (sev || '').toLowerCase();
  if (s === 'critical') return 'bg-rose-600 text-white';
  if (s === 'high') return 'bg-rose-50 text-rose-700 border border-rose-200';
  if (s === 'medium') return 'bg-amber-50 text-amber-700 border border-amber-200';
  return 'bg-slate-100 text-slate-700 border border-slate-200';
}

function SourcePill({ src }) {
  const map = {
    matched:    { label: 'Entra + HRMS', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: UserCheck },
    entra_only: { label: 'Ghost',        cls: 'bg-rose-50 text-rose-700 border-rose-200',       Icon: Ghost },
    hrms_only:  { label: 'HRMS only',    cls: 'bg-amber-50 text-amber-700 border-amber-200',    Icon: UserX },
  };
  const m = map[src] || { label: src || '—', cls: 'bg-slate-100 text-slate-700 border-slate-200', Icon: User };
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${m.cls}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

// ---------- Sub-panels ----------
function SummaryPanel({ s, hrms }) {
  if (!s) return <div className="text-sm text-slate-400 py-8 text-center">Summary unavailable.</div>;
  const m = s.metrics || {};
  const findings = s.findings || [];

  const tiles = [
    { label: 'Compliance Score', value: `${s.overall_compliance_score}%`,
      sub: s.overall_risk_rating + ' Risk',
      cls: scoreColor(s.overall_compliance_score),
      testid: 'ear-tile-compliance' },
    { label: 'MFA Status', value: m.mfa_status?.label || '—',
      sub: `${m.mfa_status?.pct ?? 0}% coverage`,
      cls: scoreColor(m.mfa_status?.pct), testid: 'ear-tile-mfa' },
    { label: 'Asset Ack',
      value: `${m.asset_ack?.acknowledged ?? 0}/${m.asset_ack?.total ?? 0}`,
      sub: m.asset_ack?.pct != null ? `${m.asset_ack.pct}% acknowledged` : 'No assets',
      cls: scoreColor(m.asset_ack?.pct), testid: 'ear-tile-assets' },
    { label: 'Training',
      value: `${m.training?.completed ?? 0}/${m.training?.assigned ?? 0}`,
      sub: m.training?.pct != null ? `${m.training.pct}% complete` : 'None assigned',
      cls: scoreColor(m.training?.pct), testid: 'ear-tile-training' },
    { label: 'SLA Compliance',
      value: m.sla?.pct != null ? `${m.sla.pct}%` : '—',
      sub: `${m.sla?.tickets_assigned ?? 0} assigned · ${m.sla?.breached ?? 0} breached`,
      cls: scoreColor(m.sla?.pct), testid: 'ear-tile-sla' },
    { label: 'Entra Hygiene',
      value: m.entra?.score != null ? `${m.entra.score}%` : 'Not matched',
      sub: m.entra?.risk_level ? `Risk: ${m.entra.risk_level}` : (m.entra?.matched ? '—' : 'No Entra link'),
      cls: scoreColor(m.entra?.score), testid: 'ear-tile-entra' },
    { label: 'AWS Hygiene',
      value: m.aws?.score != null ? `${m.aws.score}%` : 'Not matched',
      sub: m.aws?.matched ? `${m.aws.active_keys}/${m.aws.total_keys} keys active · max ${m.aws.max_key_age_days ?? '—'}d` : 'No AWS user',
      cls: scoreColor(m.aws?.score), testid: 'ear-tile-aws' },
    { label: 'Service Accounts',
      value: `${m.service_accounts?.owned ?? 0}`,
      sub: `${m.service_accounts?.review_overdue ?? 0} review overdue`,
      cls: (m.service_accounts?.review_overdue || 0) === 0 ? scoreColor(100) : scoreColor(40),
      testid: 'ear-tile-svc' },
  ];

  return (
    <div className="space-y-4">
      {/* Headline */}
      <div className={`rounded-2xl p-4 ${scoreColor(s.overall_compliance_score)}`} data-testid="ear-summary-headline-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider opacity-80">Access Review Summary</div>
            <div className="text-2xl font-bold mt-1 inline-flex items-center gap-2">
              {s.overall_compliance_score >= 80
                ? <CheckCircle2 className="w-6 h-6" />
                : <AlertTriangle className="w-6 h-6" />}
              {s.overall_compliance_score}% Compliance · {s.overall_risk_rating} Risk
            </div>
          </div>
          <div className="text-xs opacity-80 text-right">
            {findings.length === 0
              ? 'No outstanding findings.'
              : `${findings.length} finding(s) requiring attention`}
            {s.is_terminated && <div className="mt-1 font-bold">⚠ Employee is TERMINATED</div>}
          </div>
        </div>
      </div>

      {/* Metric tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ear-metric-tiles">
        {tiles.map((t) => (
          <div key={t.label} data-testid={t.testid}
               className={`rounded-2xl p-3 border ${t.cls}`}>
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{t.label}</div>
            <div className="text-lg font-bold mt-1">{t.value}</div>
            <div className="text-[11px] opacity-80 mt-0.5">{t.sub}</div>
          </div>
        ))}
      </div>

      {/* Findings */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="ear-findings">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center justify-between">
          <span>Compliance Findings</span>
          <span className="text-[10px] text-slate-500">{findings.length} total</span>
        </div>
        {findings.length === 0 ? (
          <div className="px-4 py-6 text-sm text-emerald-700 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> All checks pass — no findings.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {findings.map((f, i) => (
              <div key={i} className="px-4 py-2.5 flex items-start gap-3" data-testid={`ear-finding-${i}`}>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${sevPillClass(f.severity)}`}>
                  {f.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500">{f.category}</div>
                  <div className="text-sm text-slate-900">{f.message}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HrmsPanel({ d }) {
  const rows = [
    ['Employee ID', d.employee_id], ['Name', d.name], ['Email', d.email],
    ['Work Email', d.work_email || '—'], ['Department', d.department],
    ['Designation', d.designation], ['Role', d.role],
    ['Reporting Manager', d.reporting_manager?.name || '—'],
    ['MFA Enabled (HRMS)', d.mfa_enabled ? 'Yes' : 'No'],
    ['Last Login', (d.last_login_at || '').slice(0, 19).replace('T', ' ') || '—'],
    ['Joined', (d.joined_at || '').slice(0, 10) || '—'],
  ];
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between py-1 border-b border-slate-200 last:border-0">
          <span className="text-slate-500">{k}</span>
          <span className="font-semibold text-slate-900">{v || '—'}</span>
        </div>
      ))}
    </div>
  );
}

function TrainingPanel({ d }) {
  return (
    <div className="space-y-3">
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-xs">
        <div className="flex gap-3 text-sm">
          <span><b>{d.assignments.length}</b> assigned</span>
          <span><b>{d.completions.length}</b> completed</span>
          <span>Compliance: <b className={d.compliance_status === 'compliant' ? 'text-emerald-700' : d.compliance_status === 'partial' ? 'text-amber-700' : 'text-rose-700'}>{d.compliance_status}</b></span>
        </div>
      </div>
      <Table rows={d.assignments} empty="No training assigned" cols={[['module_name','Module'],['status','Status'],['assigned_at','Assigned'],['due_at','Due']]} dateCols={['assigned_at','due_at']} />
    </div>
  );
}

function KvPanel({ d, note }) {
  return (
    <div className="space-y-3">
      {note && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">{note}</div>}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm">
        {Object.entries(d).filter(([k]) => !['matched','access_keys','groups','roles'].includes(k)).map(([k, v]) => (
          <div key={k} className="flex justify-between py-1 border-b border-slate-200 last:border-0">
            <span className="text-slate-500 capitalize">{k.replaceAll('_', ' ')}</span>
            <span className="font-semibold text-slate-900 text-right max-w-md truncate">
              {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (v == null || v === '' ? '—' : String(v))}
            </span>
          </div>
        ))}
        {(d.access_keys || []).length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Access Keys</div>
            {d.access_keys.map((k) => (
              <div key={k.id} className="text-xs flex justify-between py-1">
                <span className="font-mono">{k.id}</span>
                <span>{k.status} · {k.age_days != null ? `${k.age_days}d old` : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Table({ rows, cols, empty, dateCols = [] }) {
  if (!rows || rows.length === 0) return <div className="text-sm text-slate-400 py-8 text-center">{empty}</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
          <tr>{cols.map(([k, l]) => <th key={k} className="text-left px-3 py-2">{l}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {cols.map(([k]) => {
                let v = r[k];
                if (dateCols.includes(k) && v) v = String(v).slice(0, k === 'ts' ? 19 : 10).replace('T', ' ');
                if (typeof v === 'boolean') v = v ? 'Yes' : 'No';
                return <td key={k} className="px-3 py-2 text-xs">{v == null || v === '' ? '—' : String(v)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportsPanel({ id }) {
  const token = localStorage.getItem('hrms_token');
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <a href={`/api/employee-access/${id}/report?format=pdf&auth=${token}`}
         data-testid="ear-report-pdf"
         className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 font-semibold text-sm">
        <FileText className="w-4 h-4" /> Download PDF
      </a>
      <a href={`/api/employee-access/${id}/report?format=xlsx&auth=${token}`}
         data-testid="ear-report-xlsx"
         className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-semibold text-sm">
        <FileSpreadsheet className="w-4 h-4" /> Download Excel
      </a>
      <a href={`/api/employee-access/${id}/report?format=csv&auth=${token}`}
         data-testid="ear-report-csv"
         className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 font-semibold text-sm">
        <Download className="w-4 h-4" /> Download CSV
      </a>
    </div>
  );
}
